import { Injectable } from '@angular/core';
import { Headers, Response } from '@angular/http';
import { Observable } from 'rxjs/Observable';
import { Subject } from 'rxjs/Subject';

import { AwsHttp } from '../aws/http';
import { Message } from '../publisher/message';
import { PublisherService } from '../publisher/publisher.service';
import { ErrorHandler } from '../restapi/error-handler';
import { UserAuthenticationMessage, UserService } from '../user/user.service';
import { Attack, AttackParams, AttackProfile } from './attack';
import { asap } from 'rxjs/scheduler/asap';

enum AttackMessageType {
    START, STOP
}

export class AttackMessage extends Message {

    public static readonly Type = AttackMessageType

    constructor(
        public attack: Attack,
        public type: AttackMessageType
    ) {
        super(attack);
    }
}

@Injectable()
export class AttackService {

    public currentAttack: Observable<Attack>;

    private userAuthenticationChannel: Observable<Message>;
    private attackChannel: Subject<Message>;

    public currentAttackObject: Attack;
    public allAttacks: Subject<Attack[]> = new Subject<Attack[]>();

    constructor(
        private http: AwsHttp,
        private errorHandler: ErrorHandler,
        private userService: UserService,
        private publisherService: PublisherService
    ) {
        this.userAuthenticationChannel = this.publisherService.subscribeToChannel('user-authentication');
        this.attackChannel = this.publisherService.createChannel('attacks');

        this.currentAttack = this.pollCurrentAttack().share();
    }

    getAvailableProfiles(): Observable<AttackProfile[]> {
        let headers = new Headers({
            'Content-Type': 'application/json'
        });
        let baseUrl = this.userService.getApiUrl();
        let url = `${baseUrl}/attackparameters/profiles`;
        return this.http
            .get(url, { headers: headers })
            .map((response: Response) => response.json() as AttackProfile[]);
    }

    start(attackParams: AttackParams): Observable<Attack> {
        let headers = new Headers({
            'Content-Type': 'application/json'
        });
        let baseUrl = this.userService.getApiUrl();
        let url = `${baseUrl}/attacks`;
        return this.http
            .post(url, JSON.stringify(attackParams), { headers: headers })
            .map((response: Response) => response.json())
            .catch((response: Response) => this.errorHandler.throwError(response))
            .do((attack: Attack) => {
                this.attackChannel.next(new AttackMessage(attack, AttackMessage.Type.START));
            });
    }

    stop(attackId: string): Observable<Attack> {
        let headers = new Headers({
            'Content-Type': 'application/json'
        });
        let baseUrl = this.userService.getApiUrl();
        let url = `${baseUrl}/attacks/${attackId}/operations/stop`;
        return this.http
            .post(url, null, { headers: headers })
            .map((response: Response) => response.json())
            .catch((response: Response) => this.errorHandler.throwError(response))
            .do((attack: Attack) => {
                this.attackChannel.next(new AttackMessage(attack, AttackMessage.Type.STOP));
            });
    }

    getAttack(attackId: string): Observable<Attack> {
        let headers = new Headers({
            'Content-Type': 'application/json'
        });
        let baseUrl = this.userService.getApiUrl();
        let url = `${baseUrl}/attacks/${attackId}`;
        return this.http
            .get(url, { headers: headers })
            .map((response: Response) => response.json())
            .catch((response: Response) => this.errorHandler.throwError(response));
    }

    getAllActiveAttacks(): Observable<Attack[]> {
        let headers = new Headers({
            'Content-Type': 'application/json'
        });
        let baseUrl = this.userService.getApiUrl();
        let url = `${baseUrl}/attacks`;
        return this.http
            .get(url, { headers: headers })
            .map((response: Response) => response.json())
            .catch((response: Response) => this.errorHandler.throwError(response));
    }

    getLatestAttack() {
        var me = this;
        return this.getAllActiveAttacks().map(attacks => {
            attacks.forEach(attack => {
                // console.log(attack);
            });
            attacks.sort((a, b) => -a.CreationDate.localeCompare(b.CreationDate));
            me.allAttacks.next(attacks);
            return attacks.length ? attacks[0] : null;
        });
    }

    private pollCurrentAttack(providedAttack?: Attack): Observable<Attack> {
        var attackGetObservable: Observable<Attack>;
        if (providedAttack) {
            attackGetObservable = Observable.of(providedAttack);
        } else if (!this.userService.currentUser) {
            attackGetObservable = Observable.of(null);
        } else if (this.currentAttackObject) {
            attackGetObservable = this.getAttack(this.currentAttackObject.ID);
        } else {
            attackGetObservable = this.getLatestAttack();
        }
        return attackGetObservable
            .catch(e => {
                console.error(e);
                return Observable.of(null);
            })
            .do(attack => this.currentAttackObject = attack)
            .switchMap(attack => {
                let shouldPoll = this.userService.currentUser && attack && ['Stopped', 'Failed'].indexOf(attack.Status) < 0;
                let pollingObservable = shouldPoll ? Observable.timer(5000).map(() => null) : Observable.empty();
                let nextUpdateObservable = Observable.merge(
                    pollingObservable,
                    this.userAuthenticationChannel
                        .filter((message: UserAuthenticationMessage) => message.type === UserAuthenticationMessage.Type.LOGIN || message.type === UserAuthenticationMessage.Type.LOGOUT)
                        .map(() => null),
                    this.attackChannel
                        .filter((message: AttackMessage) => message.type === AttackMessage.Type.START || message.type === AttackMessage.Type.STOP)
                        .map((message: AttackMessage) => message.attack)
                ).first();
                return nextUpdateObservable
                    .switchMap(attack => this.pollCurrentAttack(attack))
                    .startWith(attack)
            });
    }

}



// WEBPACK FOOTER //
// ./src/app/attack/attack.service.ts